import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Avatar, Box, Button, Paper, Typography } from "@mui/material";
import {
  WorkOrdersByTechnician,
  workOrderTechnicianOrder,
} from "../models/workOrderTypes";
import localStorageHelper from "../../../app/localStorage";
import WorkOrderQueueAPI from "../api/workOrderApi";
import packageJson from "../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";

interface TechBoardCustomColumnsProps {
  columns: WorkOrdersByTechnician[] | undefined | null; // Allow columns to be potentially undefined or null from parent
  onSave: (updatedColumns: WorkOrdersByTechnician[]) => void;
  onCancel: () => void;
}
const TechBoardCustomColumns: React.FC<TechBoardCustomColumnsProps> = ({
  columns,
  onSave,
  onCancel,
}) => {
  const user = localStorageHelper.getByKey("user");

  const [orderedColumns, setOrderedColumns] = React.useState<
    WorkOrdersByTechnician[]
  >(() => {
    let columnsAsArray: WorkOrdersByTechnician[] = [];
    if (Array.isArray(columns)) {
      columnsAsArray = columns.slice(); // Use slice() to create a safe copy
      return [...columnsAsArray].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
    } else if (
      columns &&
      typeof columns === "object" &&
      Object.keys(columns).length > 0
    ) {
      columnsAsArray = Object.values(columns);
      return [...columnsAsArray].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
    } else {
      //its empty
      return [];
    }
  });

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update displayOrder based on new positions
    const updatedColumns = items
      //.filter((x) => x.displayText !== "Unassigned")
      .map((column, index) => ({
        ...column,
        displayOrder: index + 1, // Assuming displayOrder starts at 1
      }));

    setOrderedColumns(updatedColumns);
    //Persist in the db
    const payload: workOrderTechnicianOrder[] = updatedColumns.map(
      (column) => ({
        technicianIdentity: column.identity,
        displayOrder: column.displayOrder,
        modifiedByUserIdentity: user.identity,
        modifiedDate: new Date().toISOString(),
      })
    );
    await WorkOrderQueueAPI.saveWorkOrderTechOrder(payload);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "onDragEndTechBoardColumns",
      {
        elapsed_ms: "124",
        title: "Wo status Board",
        componentName: "TechBoardCustomColumns.tsx",
        version: packageJson.version,
      }
    );
  };
  return (
    <Box sx={{ p: 3, maxWidth: "100%", maxHeight: "200px", margin: "0 auto" }}>
      <Typography variant="h5" gutterBottom>
        Configure Column Order
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Drag and drop columns to reorder them
      </Typography>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="columns" direction="horizontal">
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                my: 2,
                display: "flex",
                gap: 2, // space between columns
                overflowX: "auto", // optional: enable scrolling if too many
              }}
            >
              {Array.isArray(orderedColumns) &&
                orderedColumns
                  // .filter((column) => column.identity !== 0)
                  .map((column, index) => (
                    <Draggable
                      key={column.identity}
                      draggableId={column.identity.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          elevation={2}
                          sx={{
                            p: 2,
                            minHeight: 250,
                            minWidth: 250,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            bgcolor: "lightgray",
                            cursor: "grab",
                            "&:active": {
                              cursor: "grabbing",
                            },
                          }}
                        >
                          <Typography
                            display={"flex"}
                            sx={{
                              fontStyle: "normal",
                              fontWeight: "500",
                              fontSize: "15px",
                              color: "#121217",
                              mb: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                height: "15px",
                                width: "15px",
                                bgcolor: "#2371E7", //"primary.main", //column.color,
                                marginRight: "5px",
                                mt: "3px",
                              }}
                            >
                              <Typography variant="caption">
                                {column.displayText.charAt(0)}
                              </Typography>
                            </Avatar>
                            {column.displayText}
                          </Typography>
                          {column.displayOrder && (
                            <Typography variant="caption">
                              Order: {column.displayOrder}
                            </Typography>
                          )}
                        </Paper>
                      )}
                    </Draggable>
                  ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" sx={{ textTransform: "capitalize" }} onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" sx={{ textTransform: "capitalize" }} onClick={() => onSave(orderedColumns)}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default TechBoardCustomColumns;
